/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import $ from 'jquery';
import can from 'can';
import _ from 'lodash';

import visibilityChecker from 'scripts/utils/visibility';
import dtm from 'scripts/services/dtm';
import appConfig from 'scripts/app-config';

import template from './brightcove-video.stache';

const brightcoveOptions = _.get(window.globalSettings, 'brightcove');
const accountId = brightcoveOptions.accountId;
const playerId = brightcoveOptions.playerId;
const playerUrl = _.get(appConfig, 'urls.api.brightcove.playerUrl');

can.Component.extend({
    tag: 'brightcove-video',
    template,
    leakScope: false,
    viewModel: {
        id: _.uniqueId('video-'),
        accountId,
        playerId,
        define: {
            autoplay: {
                type: 'boolean',
                value: false
            },
            muted: {
                type: 'boolean',
                value: false
            },
            videoId: {
                type: 'string',
                value: null
            },
            continuePlaying: {
                type: 'boolean',
                value: false
            },
            dtmTrack: {
                type: 'htmlbool',
                value: false
            },
            dtmVideoName: {
                type: 'string',
                value: ''
            },
            dtmVideoLocation: {
                type: 'string',
                value: ''
            }
        },
        enableDtmTrack () {
            this.attr('dtmTrack', true);
        },
        play () {
            let player = this.attr('player');
            if (player) {
                player.play();
            }


        },
        pause () {
            let player = this.attr('player');
            if (player) {
                player.pause();
            }
        },
        isPlaying () {
            let player = this.attr('player');
            if (player) {
                return !!(player.currentTime() > 0 && !player.paused() && !player.ended() && player.readyState() > 2);
            }
            return false;
        },
    },
    events: {
        inserted () {
            this.initPlayer();
            this.viewModel.bind('videoId', () => {
                this.updateVideo();
            });
            this.relaunchVideo = function (_event, isVisible) {
                if (isVisible && this.viewModel.attr('continuePlaying')) {
                    this.viewModel.attr('continuePlaying', false);
                    this.viewModel.play();
                }
                if (!isVisible && this.viewModel.isPlaying()) {
                    this.viewModel.attr('continuePlaying', true);
                    this.viewModel.pause();
                }
            }.bind(this);
            visibilityChecker.bind('active', this.relaunchVideo);
        },

        removed () {
            visibilityChecker.unbind('active', this.relaunchVideo);

            const body = document.getElementsByTagName('body');
            if (!!body && !!body[0] && body[0].className.includes('page-gs-landing')) {
                window.dispatchEvent(new CustomEvent('bc-video-ended'));
            }
        },

        updateVideo () {
            const player = this.viewModel.attr('player');
            const videoId = this.viewModel.attr('videoId');
            
            player.catalog.getVideo(videoId, (error, video) => {
                if (error) {
                    return console.error(`Video ${videoId} can not be loaded`);
                }

                this.viewModel.dispatch({
                    type: 'metadata',
                    data: video,
                });

                player.catalog.load(video);

                this.viewModel.dispatch('videoplay');
                if (this.viewModel.attr('dtmTrack')) {
                    const videoName = this.viewModel.attr('dtmVideoName') || video.name || '';
                    dtm.trackVideoLaunch(videoName, this.viewModel.attr('dtmVideoLocation'))
                }

                const body = document.getElementsByTagName('body');
                if (!!body && !!body[0] && body[0].className.includes('page-gs-landing')) {
                    window.dispatchEvent(new CustomEvent('bc-video-started'));
                }
            });
        },

        getPlayerOptions () {

            let options = {
                preload: true,
            };

            options.muted = this.viewModel.attr('muted');
            options.autoplay = this.viewModel.attr('autoplay');

            return options
        },

        initPlayer () {
            if (this.viewModel.attr('player')) {
                return;
            }

            const video = this.element.find('video');

            if (video && video.length) {
                const accountId = this.viewModel.attr('accountId');
                const playerId = this.viewModel.attr('playerId');
                const self = this;

                $.getScript(can.sub(playerUrl, { accountId, playerId }))
                    .done(() => {
                        videojs.getPlayer(video[0], this.getPlayerOptions()).ready(function () {
                            self.viewModel.attr('player', this);
                            self.viewModel.dispatch('brightcoveready', [
                                self.viewModel,
                            ]);

                            const videoName = self.viewModel.attr('dtmVideoName') || '';
                            const bigPlayButton = self.viewModel.attr('player').getChild('bigPlayButton');
                            if (bigPlayButton) {
                                bigPlayButton.controlText(`${videoName} Play Video`);
                            }

                            this.controlBar.addClass('swiper-no-swiping');

                            this.on('fullscreenchange', () => {
                                self.viewModel.dispatch('fullscreenchange', [this.isFullscreen()]);
                            });

                            this.on('ended', () => {
                                self.viewModel.dispatch({type: 'ended'});
                            });


                            this.on('play', function (evt) {
                                self.viewModel.dispatch('play', evt);
                            });

                            self.updateVideo();
                        });
                    });
            }
        },
    }
});
